import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { Box, Button } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import AlertTitle from '@material-ui/lab/AlertTitle';
import moment from 'moment';
import { fetchOrders } from './api';
import ExtendOrderButton from './ExtendOrderButton';
import { OrderSummary } from './types';
import {
  getDaysUntil,
  getRenewalReminderCount,
  isOrderRenewable,
  RENEWAL_WINDOW_DAYS,
} from './utils';

// Orders listed per banner; the rest is counted ("and N more").
export const BANNER_ROWS = 5;

interface BannerList {
  orders: OrderSummary[];
  total: number;
}

const EMPTY: BannerList = { orders: [], total: 0 };

const formatDate = (value?: string | null) =>
  value ? moment(value).format('DD/MM/YYYY') : '—';

// Expired orders whose period has not ended yet: the consultant still cannot
// report time for them until a new order is issued. Older expired orders
// are history and stay out of the banner.
export const isOpenExpiredOrder = (
  order: Pick<OrderSummary, 'lifecycleStatus' | 'endDate'>,
  today = moment().startOf('day'),
) =>
  order.lifecycleStatus === 'EXPIRED' &&
  getDaysUntil(order.endDate, today) >= 0;

interface OrderAdminBannersProps {
  // Changes after every lifecycle action on the page, so both banners load
  // again and drop orders that were cancelled, ended or extended.
  refreshKey?: number;
  // Hides the ending banner while the list already shows those orders.
  endingListShown: boolean;
  onShowEnding: () => void;
  // The list's Issued by filter, so the banners count the same assignments.
  ownerId?: number;
}

// Banners for work order admins (ORDERS_MANAGE) at the top of All work orders
// (change request of 06.10, item 5): orders ending soon without a successor,
// each with Extend, and orders past their acceptance deadline. Consultants
// never see them; their own expired orders keep the alert in My work orders.
const OrderAdminBanners = ({
  refreshKey = 0,
  endingListShown,
  onShowEnding,
  ownerId,
}: OrderAdminBannersProps) => {
  const intl = useIntl();
  const [ending, setEnding] = useState<BannerList>(EMPTY);
  const [expired, setExpired] = useState<BannerList>(EMPTY);

  useEffect(() => {
    let active = true;
    const today = moment().format('YYYY-MM-DD');
    // Drop the previous issuer's banners; a failed load leaves none.
    setEnding(EMPTY);
    setExpired(EMPTY);
    fetchOrders(
      {
        endingWithinDays: RENEWAL_WINDOW_DAYS,
        withoutSuccessor: true,
        lifecycleStatus: 'ACCEPTED',
        ownerId,
        sort: 'endDate,asc',
      },
      0,
      BANNER_ROWS,
    ).then((result) => {
      if (active && result?.ok && result.data) {
        // An older backend that ignores the filters yields no banner.
        const total = getRenewalReminderCount(result.data);
        setEnding(
          total ? { orders: result.data.content, total } : { ...EMPTY },
        );
      }
    });
    fetchOrders(
      {
        lifecycleStatus: 'EXPIRED',
        dateFrom: today,
        ownerId,
        sort: 'decisionDeadline,desc',
      },
      0,
      BANNER_ROWS,
    ).then((result) => {
      if (active && result?.ok && result.data) {
        const { content, totalElements } = result.data;
        const matches = content.every((order) => isOpenExpiredOrder(order));
        setExpired(
          matches && totalElements
            ? { orders: content, total: totalElements }
            : { ...EMPTY },
        );
      }
    });
    return () => {
      active = false;
    };
  }, [ownerId, refreshKey]);

  const more = (list: BannerList) =>
    list.total > list.orders.length && (
      <div>
        {intl.formatMessage(
          { id: 'ORDERS.BANNER.MORE' },
          { count: list.total - list.orders.length },
        )}
      </div>
    );

  const orderLink = (orderNumber: string) => (
    <Link to={`/assignments/${encodeURIComponent(orderNumber)}`}>
      {orderNumber}
    </Link>
  );

  return (
    <>
      {ending.total > 0 && !endingListShown && (
        <Box mb={2}>
          <Alert
            severity="warning"
            aria-label={intl.formatMessage(
              { id: 'ORDERS.ENDING_BANNER.TITLE' },
              { count: ending.total, days: RENEWAL_WINDOW_DAYS },
            )}
            action={
              <Button color="inherit" size="small" onClick={onShowEnding}>
                {intl.formatMessage({ id: 'ORDERS.RENEWAL_BANNER_ACTION' })}
              </Button>
            }
          >
            <AlertTitle>
              {intl.formatMessage(
                { id: 'ORDERS.ENDING_BANNER.TITLE' },
                { count: ending.total, days: RENEWAL_WINDOW_DAYS },
              )}
            </AlertTitle>
            <div>{intl.formatMessage({ id: 'ORDERS.ENDING_BANNER.TEXT' })}</div>
            {ending.orders.map((order) => (
              <Box
                key={order.orderNumber}
                mt={1}
                display="flex"
                alignItems="center"
                flexWrap="wrap"
                gridGap={12}
              >
                <span>
                  {orderLink(order.orderNumber)}
                  {intl.formatMessage(
                    { id: 'ORDERS.ENDING_BANNER.ROW' },
                    {
                      consultant: order.consultantName,
                      client: order.clientName,
                      date: formatDate(order.effectiveEndDate || order.endDate),
                    },
                  )}
                </span>
                {isOrderRenewable(order) && (
                  <ExtendOrderButton orderNumber={order.orderNumber} />
                )}
              </Box>
            ))}
            {more(ending)}
          </Alert>
        </Box>
      )}
      {expired.total > 0 && (
        <Box mb={2}>
          <Alert
            severity="error"
            aria-label={intl.formatMessage({ id: 'ORDERS.EXPIRED.TITLE' })}
          >
            <AlertTitle>
              {intl.formatMessage({ id: 'ORDERS.EXPIRED.TITLE' })}
            </AlertTitle>
            {expired.orders.map((order) => (
              <Box key={order.orderNumber} mt={1}>
                {orderLink(order.orderNumber)}{' '}
                {intl.formatMessage(
                  { id: 'ORDERS.EXPIRED_BANNER.ROW' },
                  {
                    consultant: order.consultantName,
                    client: order.clientName,
                    date: formatDate(order.decisionDeadline),
                    owner: order.ownerName,
                  },
                )}
              </Box>
            ))}
            {more(expired)}
          </Alert>
        </Box>
      )}
    </>
  );
};

export default OrderAdminBanners;
